import axios from "axios";

const state = {
  // Admin matrix list (all forms with hat_ids); requires route.printable_forms.
  forms: [],
  // The logged-in user's own printable forms (dashboard card).
  my_forms: [],
  // Distinguishes "not fetched yet" from "fetched, user has no forms".
  my_forms_loaded: false,
};

const getters = {
  forms: (state) => state.forms,
  my_forms: (state) => state.my_forms,
  my_forms_loaded: (state) => state.my_forms_loaded,
};

const actions = {
  async fetch_forms({ commit }) {
    const response = await axios.get("/printable_forms");
    commit("SET_FORMS", response.data);
    return response.data;
  },

  // Multipart create/update. `data` is a FormData built by the caller
  // (fields + optional `template` file). Updates use POST + `_method=PUT`
  // because PHP does not parse multipart bodies on real PUT requests.
  async save_form(_, { id, data }) {
    if (id) {
      data.append("_method", "PUT");
      const response = await axios.post("/printable_forms/" + id, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    }
    const response = await axios.post("/printable_forms", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  // Saves the whole form×hat matrix in one request:
  // { <printable_form_id>: [hat_id, ...], ... }
  async save_access(_, assignments) {
    const response = await axios.post("/printable_forms/access", {
      assignments,
    });
    return response.data;
  },

  async fetch_my_forms({ commit, state }, force = false) {
    if (state.my_forms_loaded && !force) {
      return state.my_forms;
    }
    const response = await axios.get("/printable_forms/my");
    commit("SET_MY_FORMS", response.data);
    return response.data;
  },

  // Downloads the uploaded template (blob) and saves it under the
  // filename the server sends in Content-Disposition.
  async download_form(_, form) {
    const response = await axios.get(
      "/printable_forms/" + form.id + "/download",
      { responseType: "blob" }
    );
    const disposition = response.headers["content-disposition"] || "";
    const match = disposition.match(/filename="?([^";]+)"?/);
    // When Content-Disposition has no usable filename (or isn't exposed
    // cross-origin), rebuild one. Content-Type is always readable, so
    // map it back to the right extension to keep the file openable.
    const mimeExtensions = {
      "application/pdf": ".pdf",
      "application/msword": ".doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        ".docx",
      "application/vnd.ms-excel": ".xls",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        ".xlsx",
    };
    const contentType = (response.headers["content-type"] || "").split(";")[0];
    const ext =
      (form.file_path || "").match(/\.[A-Za-z0-9]+$/)?.[0] ||
      mimeExtensions[contentType] ||
      "";
    const filename = match ? match[1] : "form_" + form.form_number + ext;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

const mutations = {
  SET_FORMS(state, forms) {
    state.forms = forms;
  },

  SET_MY_FORMS(state, forms) {
    state.my_forms = forms;
    state.my_forms_loaded = true;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
